/**
 * A track's cover: the real picture when the file has one, colour only when not.
 *
 * The engine answers `/api/track/art` with the embedded picture (or the
 * folder's cover image), or 404 when there is none. Until the picture arrives,
 * and when there is none, a gradient stands in -- derived from the album or
 * artist so one album is one colour everywhere, and so a missing cover still
 * tells albums apart. The answer is remembered per track for the session, so a
 * scrolled grid does not ask twice.
 */

import { useState, useSyncExternalStore } from "react";
import { engineBase } from "../api";
import { withToken } from "../core/session";

const known = new Map<string, boolean>();

/** Bumped when a track's cover is changed, so every copy on screen reloads. */
const versions = new Map<string, number>();
const listeners = new Set<() => void>();
let tick = 0;

export function refreshArtwork(trackId: string) {
  known.delete(trackId);
  versions.set(trackId, (versions.get(trackId) ?? 0) + 1);
  tick += 1;
  listeners.forEach((fn) => fn());
}

function useArtworkTick() {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => tick
  );
}

const PALETTE: [string, string][] = [
  ["#f59e0b", "#b91c1c"], ["#14b8a6", "#1e3a8a"], ["#a855f7", "#db2777"], ["#22c55e", "#065f46"],
  ["#f97316", "#7c2d12"], ["#0ea5e9", "#312e81"], ["#eab308", "#166534"], ["#ec4899", "#7c3aed"],
  ["#64748b", "#0f172a"], ["#f43f5e", "#f59e0b"], ["#06b6d4", "#0f766e"], ["#84cc16", "#365314"],
];

export function hashOf(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

export function gradientFor(seed: string): string {
  const [a, b] = PALETTE[hashOf(seed || "nova") % PALETTE.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
}

export function Artwork({
  trackId,
  seed,
  size,
  className = "",
  rounded = 6,
  alt = "",
}: {
  trackId: string | null | undefined;
  /** What the fallback colour is derived from: album, else artist, else title. */
  seed: string;
  size?: number;
  className?: string;
  rounded?: number;
  alt?: string;
}) {
  const [, force] = useState(0);
  useArtworkTick();
  const version = trackId ? versions.get(trackId) ?? 0 : 0;
  const state = trackId ? known.get(trackId) : false;
  const style = {
    width: size,
    height: size,
    borderRadius: rounded,
    background: state ? undefined : gradientFor(seed),
  };
  return (
    <span className={`nv-art ${className}`} style={style} aria-hidden={alt ? undefined : true}>
      {trackId && state !== false && (
        <img
          key={version}
          src={withToken(`${engineBase()}/api/track/art?id=${encodeURIComponent(trackId)}${version ? `&v=${version}` : ""}`)}
          alt={alt}
          loading="lazy"
          decoding="async"
          draggable={false}
          onLoad={() => {
            if (!known.get(trackId)) {
              known.set(trackId, true);
              force((n) => n + 1);
            }
          }}
          onError={() => {
            known.set(trackId, false);
            force((n) => n + 1);
          }}
        />
      )}
    </span>
  );
}
